import {
  BonificoOrderDTO,
  BonificoOrderStatus,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { SplitAction, SplitActionButton } from "../../UI/SplitActionButton.tsx";

export type BonificoOrderActionHandlers = {
  onConfirmDraft: (order: BonificoOrderDTO) => void;
  onMarkAsPaid: (order: BonificoOrderDTO) => void;
  onIssueInvoice: (order: BonificoOrderDTO) => void;
  onClearValueDate: (order: BonificoOrderDTO) => void;
  onRefund: (order: BonificoOrderDTO) => void;
  onSetStatus: (order: BonificoOrderDTO, status: BonificoOrderStatus) => void;
  onGrantCourses: (order: BonificoOrderDTO) => void;
};

type BonificoOrderActionsLayout = {
  mainAction: SplitAction;
  otherActions: SplitAction[];
};

function getB2BActionsLayout(
  order: BonificoOrderDTO,
  handlers: BonificoOrderActionHandlers,
  refund: SplitAction,
): BonificoOrderActionsLayout {
  const registerPayment: SplitAction = {
    label: "Registra pagamento",
    onClick: () => handlers.onMarkAsPaid(order),
  };
  const grantCourses: SplitAction = {
    label: "Eroga corsi",
    onClick: () => handlers.onGrantCourses(order),
  };

  switch (order.status) {
    case BonificoOrderStatus.PendingPayment:
      return {
        mainAction: registerPayment,
        otherActions: [
          grantCourses,
          {
            label: "Segna come gestito a mano",
            onClick: () =>
              handlers.onSetStatus(order, BonificoOrderStatus.ManuallyHandled),
          },
          refund,
        ],
      };
    case BonificoOrderStatus.PartiallyPaid:
      return {
        mainAction: registerPayment,
        otherActions: [grantCourses, refund],
      };
    case BonificoOrderStatus.ManuallyHandled:
      return {
        mainAction: {
          label: "Riporta in attesa",
          tone: "neutral",
          onClick: () =>
            handlers.onSetStatus(order, BonificoOrderStatus.PendingPayment),
        },
        otherActions: [grantCourses, refund],
      };
    default:
      return { mainAction: grantCourses, otherActions: [refund] };
  }
}

function getActionsLayout(
  order: BonificoOrderDTO,
  handlers: BonificoOrderActionHandlers,
): BonificoOrderActionsLayout {
  const refund: SplitAction = {
    label: "Rimborsa",
    tone: "danger",
    onClick: () => handlers.onRefund(order),
    disabledReason: order.b2bContract
      ? "Il rimborso non è disponibile per gli ordini B2B"
      : order.status === BonificoOrderStatus.Paid
        ? undefined
        : "Il rimborso è possibile solo per gli ordini pagati",
  };
  const setStatus = (status: BonificoOrderStatus) => () =>
    handlers.onSetStatus(order, status);
  const valueDateActions: SplitAction[] = order.valueDateUtc
    ? [
        {
          label: "Modifica data valuta",
          onClick: () => handlers.onMarkAsPaid(order),
        },
        {
          label: "Rimuovi data valuta",
          onClick: () => handlers.onClearValueDate(order),
        },
      ]
    : [
        {
          label: "Segna come pagato",
          onClick: () => handlers.onMarkAsPaid(order),
        },
      ];

  if (order.b2bContract) return getB2BActionsLayout(order, handlers, refund);

  switch (order.status) {
    case BonificoOrderStatus.Draft:
      return {
        mainAction: {
          label: "Conferma ordine",
          onClick: () => handlers.onConfirmDraft(order),
        },
        otherActions: [
          ...valueDateActions,
          {
            label: "Scarta bozza",
            onClick: setStatus(BonificoOrderStatus.Discarded),
          },
          refund,
        ],
      };
    case BonificoOrderStatus.Discarded:
      return {
        mainAction: {
          label: "Ripristina bozza",
          tone: "neutral",
          onClick: setStatus(BonificoOrderStatus.Draft),
        },
        otherActions: [refund],
      };
    case BonificoOrderStatus.PendingPayment:
      return {
        mainAction: {
          label: "Segna come pagato",
          onClick: () => handlers.onMarkAsPaid(order),
        },
        otherActions: order.installmentsSettlement
          ? [refund]
          : [
              {
                label: "Segna come gestito a mano",
                onClick: setStatus(BonificoOrderStatus.ManuallyHandled),
              },
              refund,
            ],
      };
    case BonificoOrderStatus.AwaitingInvoice:
      return {
        mainAction: {
          label: "Emetti fattura",
          onClick: () => handlers.onIssueInvoice(order),
        },
        otherActions: [...valueDateActions, refund],
      };
    case BonificoOrderStatus.ManuallyHandled:
      return {
        mainAction: {
          label: "Riporta in attesa",
          tone: "neutral",
          onClick: setStatus(BonificoOrderStatus.PendingPayment),
        },
        otherActions: [refund],
      };
    default:
      return { mainAction: refund, otherActions: [] };
  }
}

export function BonificoOrderActions({
  order,
  handlers,
  disabled,
  size,
}: {
  order: BonificoOrderDTO;
  handlers: BonificoOrderActionHandlers;
  disabled?: boolean;
  size?: "sm" | "md";
}) {
  const { mainAction, otherActions } = getActionsLayout(order, handlers);
  const refundedAmount = order.refundedAmountInCents
    ? `Rimborsato €${(order.refundedAmountInCents.amount / 100).toFixed(2)}`
    : null;

  return (
    <div className="flex flex-col items-start gap-1">
      <SplitActionButton
        mainAction={mainAction}
        otherActions={otherActions}
        disabled={disabled}
        size={size}
      />
      {refundedAmount && (
        <span className="text-xs text-gray-500">{refundedAmount}</span>
      )}
    </div>
  );
}
